import { useLocation, useNavigate, useParams } from "react-router-dom";
import ColumnCard, { ColumnProp } from "@/components/ColumnCard";
import { JobSearchSolid } from "@/components/icons";

import { dateToInputFormat } from "@/utils/conversion";
import { complaintStatusIntent, complaintStatusLabel } from "@/utils/application-status";
import SlideOver from "@/components/SlideOver"
import IconButton from "@/components/buttons/IconButton"
import Box from "@/components/Box";
import Badge from "@/components/Badge";
import { useComplaint } from "../repositories/complaints-repository";

const ComplaintsDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();

    // The row hands the complaint over in `state`, but a refresh or a shared
    // link only carries the id, so always refetch and fall back to `state`
    // while the request is in flight.
    const { complaint: fetched, isLoading } = useComplaint(id);
    const complaint = fetched ?? location.state?.complaint;

    const columns: ColumnProp[] = [
        {
            heading: "Complain Type",
            content: (
                <Badge intent="default" text={complaint?.type ?? "N/A"} />
            )
        },
        {
            heading: "Submission Date",
            content: dateToInputFormat(complaint?.submissionDate)
        },
        {
            heading: "Related Application",
            content: complaint?.licenseProjectName
                ?? (complaint?.licenseId ? `Application #${complaint.licenseId}` : "Not linked")
        },
    ]

    const close = () => navigate(-1);

    return (
        <SlideOver
            title="Complaint Preview"
            icon={(
                <IconButton
                    intent="primary"
                    className="border border-zinc-200 shadow-sm p-2.5 rounded-lg"
                >
                    <JobSearchSolid className="size-6" />
                </IconButton>
            )}
            size="md"
            onClose={() => close()}
            isOpen={true}
        >

            <div className="space-y-4 md:space-y-8">
                {
                    !complaint &&
                    <Box>
                        <p className="text-sm font-light">
                            { isLoading ? "Loading complaint..." : "Complaint not found." }
                        </p>
                    </Box>
                }

                {
                    complaint &&
                    <>
                        <Box className="space-y-4">
                            <div className="flex justify-between items-start gap-4">
                                <div className="space-y-1">
                                    <h5 className="text-secondary font-semibold">
                                        # {complaint?.complaintId}
                                    </h5>
                                    <p className="text-sm font-light">
                                        {complaint?.description || "No description provided"}
                                    </p>
                                </div>
                                <Badge
                                    intent={complaintStatusIntent(complaint?.status)}
                                    text={complaintStatusLabel(complaint?.status)}
                                />
                            </div>
                            <ColumnCard columns={columns} className="grid-cols-3" />
                        </Box>

                        {/*
                          * The officer's own words. `resolutionComment` is only written by
                          * PATCH /complaints/{id}/status, and the API requires it when a
                          * complaint is resolved, rejected or closed — so this is where the
                          * applicant reads WHY. Nothing is invented when it is absent.
                          */}
                        <Box className="space-y-3">
                            <div className="flex justify-between items-center gap-4">
                                <Badge intent="default" text="Response from ZCT" />
                                {
                                    complaint?.resolvedAt && (
                                        <span className="text-xs font-light text-[#6B5C4F]">
                                            {dateToInputFormat(complaint.resolvedAt)}
                                        </span>
                                    )
                                }
                            </div>

                            {
                                complaint?.resolutionComment
                                    ? (
                                        <p className="text-sm whitespace-pre-wrap">
                                            {complaint.resolutionComment}
                                        </p>
                                    )
                                    : (
                                        <p className="text-sm font-light">
                                            No response yet — your complaint is
                                            {" "}{complaintStatusLabel(complaint?.status).toLowerCase()}.
                                            You will be notified when it is updated.
                                        </p>
                                    )
                            }
                        </Box>

                        {
                            complaint?.attachmentUrls?.length > 0 && (
                                <Box className="space-y-2">
                                    <Badge intent="default" text="Attachments" />
                                    <div className="space-y-1">
                                        {
                                            complaint.attachmentUrls.map((url: string, index: number) => (
                                                <a
                                                    key={url}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block text-sm text-primary hover:underline"
                                                >
                                                    Attachment {index + 1}
                                                </a>
                                            ))
                                        }
                                    </div>
                                </Box>
                            )
                        }
                    </>
                }
            </div>

        </SlideOver>
    )
}

export default ComplaintsDetail
